/* ==========================================================================
   TARNFELL SPACE PLANNER  ·  28 Jul 2026  (updated 19 Aug 2026)
   No framework, no dependencies. Everything below runs in the visitor's
   browser, which is why it costs nothing to serve at any volume and why
   nobody at Tarnfell has to be involved for the customer to get an answer.

   The recommendation logic deliberately includes routes that end in "do not
   buy this" and "this will not work where you live". A tool that always
   recommends something is an advertisement wearing a lab coat.
   ========================================================================== */
(function () {
  'use strict';
  var root = document.querySelector('[data-tf-planner]');
  if (!root) return;

  var UNIT = 0.2611;            // £/kWh, Ofgem cap 1 Jul to 30 Sep 2026, incl VAT
  var LOG_KG = 0.45;            // £/kg, seasoned hardwood bought by the crate

  var A = {};                   // answers
  var order = [];               // question ids actually shown, for Back
  var qs = Array.prototype.slice.call(root.querySelectorAll('[data-q]'));
  var res = root.querySelector('[data-result]');
  var prog = root.querySelector('[data-prog]');
  var back = root.querySelector('[data-back]');

  // WOOD SWITCH, 24 Sep 2026. The section sets data-tf-wood to true only
  // when a live sauna offers a heater option containing "wood burning" or
  // "wood fired". While it is false, every wood route below stays dormant:
  // no wood recommendation, no smoke control question, no log costs. The
  // moment a wood variant is published it comes back on its own, so nothing
  // here needs rebuilding when the range changes.
  var WOOD = root.getAttribute('data-tf-wood') === 'true';

  var SESSIONS = { daily: 5, often: 2.5, weekly: 1, rare: 0.4 };
  var SESSION_LABEL = { daily: 'most days', often: 'two or three times a week', weekly: 'once a week', rare: 'now and then' };

  /* ---------- flow ---------- */
  function needsSmoke() {
    if (!WOOD) return false;
    if (A.build === 'plunge') return false;
    if (A.power === 'yes' || A.power === 'would') return A.freq === 'weekly' || A.freq === 'rare';
    return true;
  }
  function nextAfter(id) {
    var seq = ['build', 'people', 'freq', 'power', 'access', 'ground', 'smoke'];
    var i = seq.indexOf(id);
    for (var j = i + 1; j < seq.length; j++) {
      if (seq[j] === 'smoke' && !needsSmoke()) continue;
      if (seq[j] === 'people' && A.build === 'plunge') { /* still relevant, keep */ }
      return seq[j];
    }
    return null;
  }
  function show(id) {
    qs.forEach(function (q) { q.classList.toggle('on', q.getAttribute('data-q') === id); });
    res.classList.remove('on');
    back.hidden = order.length < 2;
    paint(id);
    var h = root.querySelector('.tf-q.on .tf-q__title');
    if (h) h.setAttribute('tabindex', '-1'), h.focus({ preventScroll: true });
  }
  function paint(id) {
    var seq = ['build', 'people', 'freq', 'power', 'access', 'ground'];
    if (needsSmoke()) seq.push('smoke');
    prog.innerHTML = '';
    seq.forEach(function (s) {
      var i = document.createElement('i');
      if (s === id) i.className = 'on';
      else if (order.indexOf(s) > -1 && order.indexOf(s) < order.indexOf(id)) i.className = 'done';
      prog.appendChild(i);
    });
  }

  root.addEventListener('click', function (e) {
    var opt = e.target.closest('[data-val]');
    if (opt) {
      var q = opt.closest('[data-q]').getAttribute('data-q');
      A[q] = opt.getAttribute('data-val');
      if (order.indexOf(q) === -1) order.push(q);
      var nxt = nextAfter(q);
      if (nxt) show(nxt); else finish();
      return;
    }
    if (e.target.closest('[data-back]')) {
      order.pop();
      var prev = order[order.length - 1];
      if (prev) { delete A[prev]; show(prev); }
      return;
    }
    if (e.target.closest('[data-restart]')) {
      A = {}; order = ['build']; history.replaceState(null, '', location.pathname);
      show('build'); root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (e.target.closest('[data-copy]')) {
      navigator.clipboard && navigator.clipboard.writeText(location.href);
      var f = root.querySelector('[data-copied]');
      if (f) { f.classList.add('on'); setTimeout(function () { f.classList.remove('on'); }, 2200); }
    }
  });

  /* ---------- the recommendation ---------- */
  function decide() {
    var r = { checks: [], warns: [], alt: '' };
    var wantsPlunge = A.build === 'plunge' || A.build === 'both';
    var wantsSauna = A.build === 'sauna' || A.build === 'both';
    var noPower = A.power === 'no';
    var seats = A.people === 'solo' || A.people === 'two' ? 2 : (A.people === 'four' ? 4 : 6);
    var freq = A.freq || 'weekly';

    if (A.build === 'unsure') {
      if (freq === 'daily' || freq === 'often') { wantsPlunge = true; wantsSauna = false; }
      else { wantsSauna = true; wantsPlunge = false; }
    }

    var heat = 'electric';
    if (noPower) heat = 'wood';
    else if (freq === 'rare') heat = 'wood';
    else if (freq === 'weekly') heat = 'either';
    if (!WOOD) heat = 'electric';

    if (!WOOD && noPower) {
      r.head = 'Not without an electrical supply, and we would rather say so now.';
      r.band = 'Nothing we currently sell runs without one';
      r.why = (wantsPlunge && !wantsSauna)
        ? 'A chilled cold plunge holds temperature with a compressor, and a compressor needs power. There is no version of this that runs on nothing. An ice filled tub works, but it means buying and carrying ice several times a week, and almost everyone who starts that way stops within a couple of months.'
        : 'Every sauna and cold plunge we currently sell runs on electricity, and most need a dedicated circuit fitted and certified by an electrician. We would rather tell you that now than after you have chosen one.';
      r.alt = 'If a supply can reach the spot, even a short run from the house, a garage or an outbuilding, everything changes and we would happily talk it through.';
      r.checks.push('<b>Get one electrician\'s quote before ruling it out.</b> A short run from a garage or outbuilding is often far cheaper than people assume, and it unlocks the whole category.');
      r.cost = null;
      r.wantsPlunge = wantsPlunge;
      r.wantsSauna = wantsSauna;
      return r;
    }

    if (wantsPlunge && noPower) {
      r.head = 'Not without an electrical supply, and we would rather say so now.';
      r.band = 'Nothing we sell will do this well';
      r.why = 'A chilled cold plunge holds temperature with a compressor, and a compressor needs power. There is no version of this that runs on nothing. An ice filled tub works, but it means buying and carrying ice several times a week, and almost everyone who starts that way stops within a couple of months.';
      r.alt = 'If a supply can reach the spot, even a single outdoor socket on its own circuit, everything changes and we would happily talk it through. If it genuinely cannot, a wood fired hot tub or sauna is the better use of the same money, because neither needs a cable.';
      r.checks.push('<b>Get one electrician\'s quote before ruling it out.</b> A short run from a garage or outbuilding is often far cheaper than people assume, and it unlocks the whole category.');
      if (wantsSauna) r.checks.push('A wood fired sauna needs no supply at all, and would work here today.');
      r.cost = null;
      r.wantsPlunge = wantsPlunge;
      r.wantsSauna = wantsSauna;
      return r;
    }

    if (wantsPlunge && wantsSauna) {
      r.head = 'A chilled plunge and ' + (heat === 'wood' ? 'a wood fired' : heat === 'either' ? 'a wood fired or electric' : 'an electric') + ' cabin, in that order.';
      r.band = 'Roughly £7,500 to £12,000 for both, and it does not have to happen at once';
      r.why = 'Contrast is the reason most people end up with both, but the order matters. The plunge is the harder thing to get right and the thing you will use more often, so it earns the first budget. Add the cabin when the plunge has proved it is part of your week rather than a good intention.';
      r.alt = 'If the budget only stretches to one this year, take the plunge first. A sauna without cold water is a lovely room. Cold water without a sauna still does most of what people come for.';
      r.checks.push('<b>Site them within about five metres of each other.</b> The walk between the two is the whole point, and further than that in February and you will stop bothering.');
    } else if (wantsPlunge) {
      r.head = 'A chilled plunge, insulated properly, around ' + (seats <= 2 ? '£4,000 to £6,000' : '£5,000 to £7,500') + '.';
      r.band = 'Insulation matters more than chiller size. It decides your bill.';
      r.why = 'At ' + SESSION_LABEL[freq] + ', the number that will govern your experience is not how cold it goes but how well it holds. A well insulated tub with a modest chiller costs less to run, makes less noise and puts fewer hours on the compressor than a thin tub with a powerful one.';
      r.alt = 'A cheaper uninsulated tub with an add on chiller will get you there for around half the money. It will also run its compressor two or three times as much, which you will hear and pay for, and the seals and pump will reach the end of their life sooner.';
      r.checks.push('<b>Ask for the cooling capacity at 25°C ambient, not just a headline temperature.</b> A figure quoted at twenty degrees tells you very little about a July afternoon.');
      r.checks.push('<b>Ask whether the lid and the pipework are insulated,</b> not only the shell. A bare lid on a plunge is a bare loft on a house.');
      r.checks.push('<b>Ask the noise level in decibels at one metre.</b> This decides whether it can sit under a bedroom window or near a neighbour.');
    } else {
      var sizeTxt = seats <= 2 ? 'two person' : seats === 4 ? 'four person' : 'five or six person';
      r.head = 'A ' + sizeTxt + ' ' + (heat === 'wood' ? 'wood fired' : heat === 'either' ? 'cabin, and the heat source is genuinely your call' : 'electric') + ' sauna.';
      r.band = seats <= 2 ? 'Roughly £3,200 to £6,000' : seats === 4 ? 'Roughly £5,500 to £9,500' : 'Roughly £8,000 to £13,000';
      if (!WOOD) {
        if (freq === 'daily' || freq === 'often') {
          r.why = 'You have told us you will use it ' + SESSION_LABEL[freq] + ', so what decides whether it gets used is how quickly it is ready. A smaller cabin heats faster and costs less each time, so size it for the people who will use it most weeks rather than the most you might ever have.';
        } else {
          r.why = 'At ' + SESSION_LABEL[freq] + ' this is an occasion rather than a habit, which leaves room to choose on the experience: a traditional heater with stones and steam, infrared at a lower air temperature, or a hybrid cabin that does both.';
        }
        r.alt = 'If the garden does not suit, the same choice exists indoors: traditional, infrared and hybrid cabins built for a spare room or a garage.';
      } else if (heat === 'electric') {
        r.why = 'You have told us you will use it ' + SESSION_LABEL[freq] + ', and that single answer decides this. A wood fire takes about forty minutes of attention before you get in, and on a wet Tuesday that forty minutes is the difference between using it and not. Electric removes the decision.';
        r.alt = 'Wood fired is the better experience, and we will not pretend otherwise. The heat is drier and more alive and the ritual is a real part of it. If you were using it once a week as a deliberate occasion, we would have sent you that way.';
      } else if (heat === 'wood') {
        r.why = 'At ' + SESSION_LABEL[freq] + ' this is an occasion rather than a habit, and that is exactly where wood earns its keep. The fire is part of the evening rather than an obstacle to it, and you avoid an electrician and a cable run entirely.';
        r.alt = 'Electric would suit you if that frequency crept upward. The honest test is whether you would still lay a fire in the rain in February. Plenty of people would. Plenty know they would not.';
      } else {
        r.why = 'Once a week sits exactly on the line, so this one really is a preference rather than a right answer. Wood gives you the better hour. Electric gives you more hours, because it removes the forty minutes of fire laying that quietly cancels sessions.';
        r.alt = 'If you have a log supply and enjoy the process, take wood. If you are already imagining the evenings you would talk yourself out of it, take electric and do not feel you have compromised.';
      }
      if (seats >= 5) r.checks.push('<b>A five or six person cabin costs noticeably more to heat</b> and takes longer to get there. Most people who buy this size use it with two or three, and would have been happier with a smaller, faster cabin.');
      if (seats <= 2) r.checks.push('A two person cabin reaches temperature in roughly half the time of a four, which over a decade is the difference between a habit and an intention.');
    }

    if (A.access === 'narrow') {
      r.warns.push('<b>Under 900mm is the constraint that decides your options.</b> An assembled barrel will not pass. You need a cabin that ships in panels and is built in place, and you should confirm the panel width with us before ordering anything.');
    } else if (A.access === 'none') {
      r.warns.push('<b>With no route through, this has to be craned over.</b> That is far more ordinary than it sounds and often cheaper than people fear, but it must be priced and booked in advance rather than discovered on delivery day.');
    } else if (A.access === 'medium') {
      r.checks.push('<b>Measure the narrowest point again, at the pinch,</b> not at the gate. A drainpipe or a meter box three metres past the gate catches more deliveries than the gate ever does.');
    }

    if (A.ground === 'decking' && (wantsPlunge || A.build === 'unsure')) {
      r.warns.push('<b>Decking and a filled plunge is the combination that needs checking properly.</b> Water weighs a kilogram a litre, so a six hundred litre tub is over half a tonne before anyone gets in. Have someone look at the joists and the spans before you order.');
    } else if (A.ground === 'decking') {
      r.checks.push('Decking will usually carry a cabin, but check the joists and make sure air can still move underneath. Trapped damp under a sauna is the commonest way these fail early.');
    }
    if (A.ground === 'soft') {
      r.checks.push('<b>Lawn or gravel is not a base.</b> You will need a level, free draining slab or a proper bearer arrangement, laid and cured before delivery. This is the least glamorous line in the whole project and the one that decides whether you still own it in ten years.');
    }
    if (A.ground === 'none') {
      r.checks.push('<b>The base is your first job, not your last.</b> It needs to be finished and cured before anything is delivered. Get that quoted now, because it often sets the timeline for everything else.');
    }
    if (A.ground === 'paving') {
      r.checks.push('Paving is usually fine if it is properly bedded and level. Check it drains away from the structure rather than towards it.');
    }

    if (needsSmoke() && heat !== 'electric') {
      if (A.smoke === 'yes') {
        r.warns.push('<b>In a smoke control area you cannot burn wood unless the stove itself is on the Defra exempt list.</b> Government guidance is explicit that a roofed garden structure with a chimney is covered by the same rule as your house. Very few sauna stoves are on that list. Confirm the specific model in writing before you spend anything.');
      } else if (A.smoke === 'unsure') {
        r.checks.push('<b>Ring your council and ask whether your address is in a smoke control area.</b> It takes one phone call. If it is, a wood stove has to be Defra exempt, and most are not. This is a fifteen minute check that occasionally saves several thousand pounds.');
      }
    }

    var s = SESSIONS[freq];
    if (wantsPlunge && !wantsSauna) {
      var lo = Math.round(2.4 * UNIT * 365), hi = Math.round(4.6 * UNIT * 365);
      r.cost = { fig: '£' + lo + ' to £' + hi + ' a year',
        sub: 'Electricity to hold a plunge at temperature all year, well insulated at the lower end and thinly insulated at the upper. Add roughly £80 to £150 a year for filters and water care. Counterintuitively it costs less in January than in July, because the air is helping rather than fighting.' };
    } else if (heat === 'wood') {
      var kg = seats <= 2 ? 8 : seats === 4 ? 12 : 16;
      var per = kg * LOG_KG;
      r.cost = { fig: '£' + per.toFixed(2) + ' a session, about £' + Math.round(per * s * 52) + ' a year',
        sub: 'Based on ' + kg + 'kg of seasoned hardwood at crate prices, used ' + SESSION_LABEL[freq] + '. Free if you have your own wood. Considerably more if you buy small bags from a forecourt, which is the mistake most new owners make once.' };
    } else {
      var kwh = seats <= 2 ? 4.5 : seats === 4 ? 6.5 : 9;
      var perE = kwh * UNIT;
      r.cost = { fig: '£' + perE.toFixed(2) + ' a session, about £' + Math.round(perE * s * 52) + ' a year',
        sub: 'Heating from cold plus an hour of use, at the Ofgem capped rate of 26.11p a unit. Insulation quality moves this figure far more than the make of heater does.' };
      if (wantsPlunge) r.cost.sub += ' The plunge adds roughly £230 to £440 a year on top, held year round.';
    }
    r.wantsPlunge = wantsPlunge;
    r.wantsSauna = wantsSauna;
    return r;
  }

  /* ---------- render ---------- */
  function finish() {
    var r = decide();
    var ctaHref, ctaLabel;
    if (!r.cost) {
      ctaHref = "/pages/contact";
      ctaLabel = "Talk to a person";
    } else if (r.wantsPlunge && r.wantsSauna) {
      ctaHref = "/collections/cold-plunge-baths";
      ctaLabel = "Explore cold plunges";
    } else if (r.wantsPlunge) {
      ctaHref = "/collections/cold-plunge-baths";
      ctaLabel = "Explore cold plunges";
    } else if (r.wantsSauna) {
      ctaHref = "/collections/saunas";
      ctaLabel = "Explore saunas";
    } else {
      ctaHref = "/collections/saunas";
      ctaLabel = "Explore saunas";
    }
    var el = function (s) { return res.querySelector(s); };
    el('[data-head]').innerHTML = r.head;
    el('[data-band]').innerHTML = r.band;
    el('[data-why]').innerHTML = r.why;

    var altW = el('[data-alt-wrap]');
    if (r.alt) { altW.hidden = false; el('[data-alt]').innerHTML = r.alt; } else altW.hidden = true;

    var wW = el('[data-warn-wrap]');
    if (r.warns.length) { wW.hidden = false; el('[data-warns]').innerHTML = r.warns.map(function (w) { return '<li>' + w + '</li>'; }).join(''); }
    else wW.hidden = true;

    var cW = el('[data-check-wrap]');
    if (r.checks.length) { cW.hidden = false; el('[data-checks]').innerHTML = r.checks.map(function (c) { return '<li>' + c + '</li>'; }).join(''); }
    else cW.hidden = true;

    var costW = el('[data-cost-wrap]');
    if (r.cost) { costW.hidden = false; el('[data-cost-fig]').innerHTML = r.cost.fig; el('[data-cost-sub]').innerHTML = r.cost.sub; }
    else costW.hidden = true;

    // Build the plain text summary for the email
    var QLABELS = { build: 'What you were after', people: 'How many people', freq: 'How often', power: 'Electrical supply', access: 'Narrowest access point', ground: 'What it stands on', smoke: 'Smoke control area' };
    var ALABELS = {
      build: { plunge: 'A cold plunge', sauna: 'A sauna', both: 'Both, for contrast', unsure: 'Not sure yet' },
      people: { solo: 'Usually just me', two: 'Two of us', four: 'Three or four', many: 'Five or more' },
      freq: { daily: 'Most days', often: 'Two or three times a week', weekly: 'About once a week', rare: 'Now and then, mostly weekends' },
      power: { yes: 'Yes, already there', would: 'No, but would have one installed', no: 'No, and rather not' },
      access: { wide: 'Wider than 1.2 metres', medium: '900mm to 1.2 metres', narrow: 'Under 900mm', none: 'No route through at all' },
      ground: { slab: 'Concrete or a proper slab', paving: 'Paving or a patio', decking: 'Decking', soft: 'Lawn or gravel', none: 'Nothing yet' },
      smoke: { yes: 'Yes', no: 'No', unsure: 'Not sure' }
    };
    var qSeq = ['build', 'people', 'freq', 'power', 'access', 'ground', 'smoke'];

    var answerRows = "";
    var plainParts = [];
    for (var qi = 0; qi < qSeq.length; qi++) {
      var qk = qSeq[qi];
      if (A[qk] && ALABELS[qk] && ALABELS[qk][A[qk]]) {
        var qLabel = QLABELS[qk];
        var aLabel = ALABELS[qk][A[qk]];
        answerRows += "<tr><td style='padding:9px 0;border-bottom:1px solid #E5DFD2;font-size:12px;color:#8b8575;width:42%;vertical-align:top;'>" + qLabel + "</td><td style='padding:9px 0;border-bottom:1px solid #E5DFD2;font-size:14px;color:#26241F;vertical-align:top;'>" + aLabel + "</td></tr>";
        plainParts.push(qLabel + ": " + aLabel);
      }
    }
    var answersTableHtml = "<p style='margin:0 0 6px 0;font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:#8b8575;'>Based on your answers</p><table width='100%' cellpadding='0' cellspacing='0' style='margin:0 0 26px 0;'>" + answerRows + "</table>";

    var recHtml = "<p style='margin:0 0 4px 0;font-size:11px;letter-spacing:0.06em;text-transform:uppercase;color:#8b8575;'>What we would put in the garden</p>" +
      "<h2 style='margin:0 0 8px 0;font-size:21px;font-weight:400;color:#26241F;line-height:1.3;'>" + r.head + "</h2>" +
      "<p style='margin:0 0 16px 0;font-size:15px;color:#26241F;font-weight:600;'>" + r.band + "</p>" +
      "<p style='margin:0 0 20px 0;font-size:14px;color:#4a463d;line-height:1.6;'>" + r.why + "</p>";
    if (r.alt) {
      recHtml += "<p style='margin:0 0 4px 0;font-size:12px;font-weight:600;color:#26241F;'>The honest alternative</p><p style='margin:0 0 20px 0;font-size:14px;color:#4a463d;line-height:1.6;'>" + r.alt + "</p>";
    }
    if (r.cost) {
      recHtml += "<p style='margin:0 0 4px 0;font-size:12px;font-weight:600;color:#26241F;'>What it costs to live with</p><p style='margin:0 0 4px 0;font-size:15px;color:#26241F;font-weight:600;'>" + r.cost.fig + "</p><p style='margin:0 0 20px 0;font-size:13px;color:#4a463d;line-height:1.6;'>" + r.cost.sub + "</p>";
    }
    if (r.warns.length) {
      recHtml += "<p style='margin:0 0 4px 0;font-size:12px;font-weight:600;color:#26241F;'>Settle these before you spend anything</p><ul style='margin:0 0 20px 0;padding-left:18px;'>";
      for (var wi = 0; wi < r.warns.length; wi++) recHtml += "<li style='font-size:13px;color:#4a463d;line-height:1.6;margin-bottom:6px;'>" + r.warns[wi] + "</li>";
      recHtml += "</ul>";
    }
    // 'Worth checking' intentionally omitted from the email (kept on-site only) to keep the email shorter

    var summaryHtml = recHtml + "<div style='margin:24px 0;border-top:1px solid #E5DFD2;'></div>" + answersTableHtml;

    var summaryText = 'Space planner result. ' + plainParts.join('; ') +
      '. Recommended: ' + r.head.replace(/<[^>]+>/g, '') +
      ' Band: ' + r.band.replace(/<[^>]+>/g, '');

    // Feed hidden textarea for Shopify contact form fallback
    var sum = res.querySelector('[data-summary]');
    if (sum) sum.value = summaryText;

    qs.forEach(function (q) { q.classList.remove('on'); });
    res.classList.add('on');
    back.hidden = true;
    prog.innerHTML = '';
    history.replaceState(null, '', location.pathname + '#' + serialise());
    res.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Wire the send form now that we have the summary
    wireSendForm(summaryText, summaryHtml, ctaHref, ctaLabel);
  }

  /* ---------- email send form ---------- */
  function wireSendForm(summaryText, summaryHtml, ctaHref, ctaLabel) {
    var form = res.querySelector('#TfPlannerSend');
    if (!form || form.dataset.wired) return;
    form.dataset.wired = '1';

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var emailInput = form.querySelector('[type="email"]');
      var submitBtn  = form.querySelector('[type="submit"]');
      var okMsg      = form.querySelector('.tf-send__ok');
      var email      = emailInput ? emailInput.value.trim() : '';

      if (!email) return;

      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending...';

      fetch('https://hook.eu1.make.com/0ygqs2j6raw3ffbsw8ecqo61wy8c17hi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          summary: summaryText,
          summaryHtml: summaryHtml,
          ctaHref: location.origin + ctaHref,
          ctaLabel: ctaLabel,
          shareUrl: location.href
        })
      })
      .then(function (r) {
        if (!r.ok) throw new Error('Send failed');
        if (okMsg) {
          okMsg.textContent = 'Sent. If anything in it does not fit your garden, reply to that email and we will work it out.';
          okMsg.style.display = 'block';
        }
        submitBtn.disabled = false;
        submitBtn.textContent = 'Send';
        emailInput.value = '';
      })
      .catch(function () {
        // Make webhook unreachable — fall back to Shopify contact form so Grant still gets notified
        submitBtn.disabled = false;
        submitBtn.textContent = 'Send';
        form.dataset.wired = 'done';
        form.submit();
      });
    });
  }

  /* ---------- shareable state ---------- */
  function serialise() { return Object.keys(A).map(function (k) { return k + '=' + A[k]; }).join('&'); }
  function restore() {
    var h = location.hash.replace(/^#/, '');
    if (!h) return false;
    A = {};
    h.split('&').forEach(function (p) { var kv = p.split('='); if (kv.length === 2) A[kv[0]] = kv[1]; });
    if (!A.build) { A = {}; return false; }
    order = Object.keys(A);
    finish();
    return true;
  }

  window.addEventListener('hashchange', function () {
    A = {}; order = ['build'];
    if (!restore()) show('build');
  });

  root.removeAttribute('data-nojs');
  order = ['build'];
  if (!restore()) show('build');
})();
